
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, RefreshCw, Download, Upload, PlusCircle, Columns } from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import CSVImportDialog from "@/components/CSVImportDialog";
import { ColumnConfig } from "./ReusableTablePage";

interface TableToolbarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  columns: ColumnConfig[];
  visibleColumns: string[];
  onColumnVisibilityChange: (columns: string[]) => void;
  onRefresh: () => void;
  onImport?: (rows: any[]) => void;
  onExport?: () => void;
  onExportTemplate?: () => void;
  onAddNew?: () => void;
  addButtonText: string;
}

export const TableToolbar: React.FC<TableToolbarProps> = ({
  searchQuery,
  setSearchQuery,
  columns,
  visibleColumns,
  onColumnVisibilityChange,
  onRefresh,
  onImport,
  onExport,
  onExportTemplate,
  onAddNew,
  addButtonText,
}) => {
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  const handleToggleColumn = (key: string) => {
    if (visibleColumns.includes(key)) {
      onColumnVisibilityChange(visibleColumns.filter(col => col !== key));
    } else {
      onColumnVisibilityChange([...visibleColumns, key]);
    }
  };

  const handleImport = (data: any[]) => {
    console.log("[TableToolbar] Import data:", data);
    if (onImport) {
      onImport(data);
    }
    setIsImportDialogOpen(false);
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between gap-3 mb-4">
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search..."
          className="pl-9 w-full"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" title="Columns">
              <Columns className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 max-h-[400px] overflow-auto">
            <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {columns.map((column) => (
              <DropdownMenuCheckboxItem
                key={column.key}
                checked={visibleColumns.includes(column.key)}
                onCheckedChange={() => handleToggleColumn(column.key)}
              >
                {column.header}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button 
          variant="outline" 
          size="icon" 
          onClick={onRefresh} 
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>

        {onExport && (
          <Button 
            variant="outline" 
            size="icon" 
            onClick={onExport} 
            title="Export"
          >
            <Download className="h-4 w-4" />
          </Button>
        )}

        {onExportTemplate && (
          <Button
            variant="outline"
            size="icon"
            onClick={onExportTemplate}
            title="Export Template"
          >
            <Download className="h-4 w-4" />
          </Button>
        )}

        {onImport && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIsImportDialogOpen(true)}
            title="Import"
          >
            <Upload className="h-4 w-4" />
          </Button>
        )}

        {onAddNew && (
          <Button 
            onClick={onAddNew}
            className="flex items-center gap-2"
          >
            <PlusCircle className="h-4 w-4" />
            <span>{addButtonText}</span>
          </Button>
        )}
      </div>

      {onImport && (
        <CSVImportDialog
          open={isImportDialogOpen}
          onOpenChange={setIsImportDialogOpen}
          importType="hardware"
          onImport={handleImport}
        />
      )}
    </div>
  );
};
